/**
 * @fileoverview Reusable confirmation modal component.
 * @summary Displays a modal dialog for user confirmations.
 *
 * @package KaraoNow
 * @module apps/front/src/components/ConfirmModal
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'

/**
 * @summary Props for the ConfirmModal component.
 */
interface ConfirmModalProps {
  /**
   * @summary Whether the modal is open.
   */
  isOpen: boolean

  /**
   * @summary Title of the modal.
   */
  title: string

  /**
   * @summary Message content of the modal.
   */
  message: string

  /**
   * @summary Label for the confirm button.
   * @default "Confirm"
   */
  confirmLabel?: string

  /**
   * @summary Label for the cancel button.
   * @default "Cancel"
   */
  cancelLabel?: string

  /**
   * @summary Variant of the confirm button (danger, warning, primary).
   * @default "primary"
   */
  variant?: 'danger' | 'warning' | 'primary' | 'info'

  /**
   * @summary Callback when the user confirms.
   */
  onConfirm?: () => void

  /**
   * @summary Callback when the user cancels or closes the modal.
   */
  onCancel: () => void

  /**
   * @summary Whether the confirm action is in progress (loading state).
   * @default false
   */
  isLoading?: boolean

  /**
   * @summary If true, displays only an informational message without action buttons.
   * @default false
   */
  infoOnly?: boolean
}

/**
 * @summary Reusable confirmation modal component.
 * @param props Component props.
 * @returns The confirmation modal component.
 */
export function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  onConfirm,
  onCancel,
  isLoading = false,
  infoOnly = false,
}: ConfirmModalProps) {
  const { t } = useTranslation()

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onCancel()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, isLoading, onCancel])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  const variantClasses = {
    danger:
      'bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700',
    warning:
      'bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-600 dark:hover:bg-yellow-700',
    primary:
      'bg-(--color-primary) hover:bg-(--color-primary-600)',
    info:
      'bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700',
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isLoading) {
          onCancel()
        }
      }}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 dark:bg-black/70" />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full z-10">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-(--color-border)">
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full ${
                variant === 'danger'
                  ? 'bg-red-100 dark:bg-red-900'
                  : variant === 'warning'
                    ? 'bg-yellow-100 dark:bg-yellow-900'
                    : variant === 'info'
                      ? 'bg-blue-100 dark:bg-blue-900'
                      : 'bg-(--color-primary-100) dark:bg-(--color-primary-900)'
              }`}
            >
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className={`w-5 h-5 ${
                  variant === 'danger'
                    ? 'text-red-600 dark:text-red-400'
                    : variant === 'warning'
                      ? 'text-yellow-600 dark:text-yellow-400'
                      : variant === 'info'
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-(--color-primary-600) dark:text-(--color-primary-400)'
                }`}
              />
            </div>
            <h3 className="text-lg font-semibold text-(--color-text-primary)">{title}</h3>
          </div>
          {!isLoading && (
            <button
              onClick={onCancel}
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-(--color-text-secondary)">{message}</p>
        </div>

        {/* Footer */}
        {infoOnly ? (
          <div className="flex justify-end p-6 border-t border-(--color-border)">
            <button onClick={onCancel} className="btn btn-primary w-full sm:w-auto">
              {t('common.close') || cancelLabel}
            </button>
          </div>
        ) : (
          <div className="flex flex-col-reverse gap-3 p-6 border-t border-(--color-border) sm:flex-row sm:justify-end">
            <button onClick={onCancel} disabled={isLoading} className="btn btn-secondary w-full sm:w-auto">
              {cancelLabel}
            </button>
            {onConfirm && (
              <button
                onClick={onConfirm}
                disabled={isLoading}
                className={`btn ${variantClasses[variant]} text-white w-full sm:w-auto`}
              >
                {isLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                    <span className="truncate">{confirmLabel}</span>
                  </>
                ) : (
                  <span className="truncate">{confirmLabel}</span>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

